// src/components/wells/FloodInundationAlert.js
// --- UPDATE: hooks for state, memoisation, refs and timers
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// --- END UPDATE
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Badge,
  HStack,
  Text,
  Button,
  Collapse,
  useColorModeValue,
  // --- UPDATE
  Icon,
  IconButton,
  Tooltip,
  Wrap,
  WrapItem,
  // --- END UPDATE
} from '@chakra-ui/react';
import { MdWarning, MdNotificationsActive } from 'react-icons/md';
// --- UPDATE
import {
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdExpandMore,
  MdExpandLess,
  MdMyLocation,
  MdBuild,
  MdAccessTime,
  MdPlumbing,
  MdInfoOutline,
} from 'react-icons/md';
// --- END UPDATE

// --- UPDATE: severity themes (HIGH keeps the original red.600 styling; legacy alerts without
// a `severity` field render exactly as before)
const SEVERITY_THEME = Object.freeze({
  CRITICAL: {
    bg: 'red.700',
    border: 'red.200',
    shadow: 'rgba(197, 48, 48, 0.6)',
    title: 'CRITICAL WATER OVERFLOW',
    icon: MdNotificationsActive,
  },
  HIGH: {
    bg: 'red.600',
    border: 'red.300',
    shadow: 'rgba(229, 62, 62, 0.45)',
    title: 'HIGH WATER OVERFLOW RISK',
    icon: MdWarning,
  },
  WARNING: {
    bg: 'orange.500',
    border: 'orange.200',
    shadow: 'rgba(221, 107, 32, 0.4)',
    title: 'ELEVATED WATER LEVEL',
    icon: MdWarning,
  },
});

const getTheme = (severity) => SEVERITY_THEME[severity] || SEVERITY_THEME.HIGH;

const alertSignature = (alert) =>
  `${alert.wellId}|${alert.severity || 'HIGH'}|${(alert.impactedRoads || [])
    .map((r) => String(r.roadId))
    .sort()
    .join(',')}`;

const formatRelativeTime = (timestampMs, nowMs) => {
  if (!Number.isFinite(timestampMs)) return null;
  const diffSec = Math.max(0, Math.round((nowMs - timestampMs) / 1000));
  if (diffSec < 45) return 'just now';
  const mins = Math.round(diffSec / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const formatDistance = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${m}m`);

const formatLevel = (alert) => {
  if (Number.isFinite(alert.levelValue)) return `${alert.levelValue}%`;
  const raw = alert.reading?.level;
  return raw !== undefined && raw !== null && raw !== '' ? String(raw).toUpperCase() : null;
};

const RELATIVE_TIME_TICK_MS = 30000;
// --- END UPDATE

const FloodInundationAlert = ({
  roadAlerts = [],
  onFocusLocation,
  // --- UPDATE: optional enterprise props (all backwards compatible)
  highRiskPipes = [],
  summary = null,
  onDismiss,
  onDispatchCrew,
  onViewDetails,
  maxVisibleRoads = 3,
  isDismissible = true,
  showMutedIndicator = true,
  containerProps = {},
  // --- END UPDATE
}) => {
  // --- UPDATE: all hooks run before any early return (Rules of Hooks)
  const isMounted = useRef(false);
  const renderCount = useRef(0);
  renderCount.current += 1;

  const mutedBg = useColorModeValue('white', 'gray.800');
  const mutedColor = useColorModeValue('gray.700', 'gray.100');
  const mutedBorder = useColorModeValue('red.200', 'red.700');
  const focusRing = useColorModeValue('whiteAlpha.900', 'whiteAlpha.800');

  const [activeIndex, setActiveIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => new Set());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const safeAlerts = useMemo(
    () =>
      (Array.isArray(roadAlerts) ? roadAlerts : []).filter(
        (a) => a && Array.isArray(a.impactedRoads) && a.impactedRoads.length > 0
      ),
    [roadAlerts]
  );

  const signatures = useMemo(() => safeAlerts.map(alertSignature), [safeAlerts]);

  const visibleAlerts = useMemo(
    () => safeAlerts.filter((_, i) => !dismissed.has(signatures[i])),
    [safeAlerts, signatures, dismissed]
  );

  // Forget dismissals for alerts that have cleared or changed, so escalations reappear
  useEffect(() => {
    setDismissed((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(signatures);
      const next = new Set([...prev].filter((s) => live.has(s)));
      return next.size === prev.size ? prev : next;
    });
  }, [signatures]);

  const safeIndex = visibleAlerts.length ? Math.min(activeIndex, visibleAlerts.length - 1) : 0;
  const topAlert = visibleAlerts[safeIndex] || null;
  const topSignature = topAlert ? alertSignature(topAlert) : null;

  useEffect(() => {
    setIsExpanded(false);
  }, [topSignature]);

  const hasTimestamps = useMemo(
    () => visibleAlerts.some((a) => Number.isFinite(a.readingTimestamp)),
    [visibleAlerts]
  );

  useEffect(() => {
    if (!hasTimestamps) return undefined;
    const id = setInterval(() => {
      if (isMounted.current) setNow(Date.now());
    }, RELATIVE_TIME_TICK_MS);
    return () => clearInterval(id);
  }, [hasTimestamps]);

  const theme = useMemo(() => getTheme(topAlert?.severity), [topAlert]);

  const roadsToShow = useMemo(() => {
    if (!topAlert) return [];
    return isExpanded ? topAlert.impactedRoads : topAlert.impactedRoads.slice(0, maxVisibleRoads);
  }, [topAlert, isExpanded, maxVisibleRoads]);

  const totalRoads = topAlert
    ? Math.max(topAlert.totalImpactedRoads || 0, topAlert.impactedRoads.length)
    : 0;
  const hiddenRoadCount = topAlert ? totalRoads - roadsToShow.length : 0;
  const truncatedByModel = topAlert ? totalRoads - topAlert.impactedRoads.length : 0;

  const pipesForWell = useMemo(
    () =>
      topAlert && Array.isArray(highRiskPipes)
        ? highRiskPipes.filter((p) => String(p.wellId) === String(topAlert.wellId))
        : [],
    [highRiskPipes, topAlert]
  );

  const relativeTime = topAlert ? formatRelativeTime(topAlert.readingTimestamp, now) : null;
  const levelLabel = topAlert ? formatLevel(topAlert) : null;
  // --- END UPDATE

  const affectedRoadsText = useMemo(
    () =>
      roadsToShow
        .map((r) => `${r.roadName} (~${formatDistance(r.distanceM)})`)
        .join(' · '),
    [roadsToShow]
  );

  // --- UPDATE: handlers
  const handlePrev = useCallback(() => {
    setActiveIndex((i) => (i - 1 + visibleAlerts.length) % Math.max(visibleAlerts.length, 1));
  }, [visibleAlerts.length]);

  const handleNext = useCallback(() => {
    setActiveIndex((i) => (i + 1) % Math.max(visibleAlerts.length, 1));
  }, [visibleAlerts.length]);

  const handleToggleExpanded = useCallback(() => setIsExpanded((v) => !v), []);

  const handleLocateWell = useCallback(() => {
    if (!topAlert || !onFocusLocation) return;
    onFocusLocation(topAlert.lat, topAlert.lng, {
      type: 'well',
      wellId: topAlert.wellId,
      alert: topAlert,
    });
  }, [topAlert, onFocusLocation]);

  const handleLocateRoad = useCallback(
    (road) => {
      if (!onFocusLocation || !topAlert) return;
      const lat = Number.isFinite(road.lat) ? road.lat : topAlert.lat;
      const lng = Number.isFinite(road.lng) ? road.lng : topAlert.lng;
      onFocusLocation(lat, lng, { type: 'road', roadId: road.roadId, wellId: topAlert.wellId });
    },
    [onFocusLocation, topAlert]
  );

  const handleDismiss = useCallback(() => {
    if (!topAlert) return;
    const sig = alertSignature(topAlert);
    setDismissed((prev) => new Set(prev).add(sig));
    if (onDismiss) onDismiss(topAlert);
  }, [topAlert, onDismiss]);

  const handleRestore = useCallback(() => {
    setDismissed(new Set());
    setActiveIndex(0);
  }, []);

  const handleDispatch = useCallback(() => {
    if (topAlert && onDispatchCrew) onDispatchCrew(topAlert);
  }, [topAlert, onDispatchCrew]);

  const handleViewDetails = useCallback(() => {
    if (topAlert && onViewDetails) onViewDetails(topAlert);
  }, [topAlert, onViewDetails]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowLeft' && visibleAlerts.length > 1) {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowRight' && visibleAlerts.length > 1) {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'Escape' && isDismissible) {
        e.preventDefault();
        handleDismiss();
      }
    },
    [visibleAlerts.length, handlePrev, handleNext, handleDismiss, isDismissible]
  );
  // --- END UPDATE

  if (!roadAlerts || roadAlerts.length === 0) return null;

  // --- UPDATE: everything dismissed → compact restore pill instead of nothing
  if (!topAlert) {
    if (!showMutedIndicator || safeAlerts.length === 0) return null;
    return (
      <Box
        position="absolute"
        top="16px"
        left="50%"
        transform="translateX(-50%)"
        zIndex={1400}
        pointerEvents="auto"
        {...containerProps}
      >
        <Button
          size="xs"
          leftIcon={<Icon as={MdNotificationsActive} />}
          bg={mutedBg}
          color={mutedColor}
          borderWidth="1px"
          borderColor={mutedBorder}
          borderRadius="full"
          boxShadow="md"
          onClick={handleRestore}
          aria-label={`Show ${safeAlerts.length} muted flood alerts`}
        >
          {safeAlerts.length} flood alert{safeAlerts.length > 1 ? 's' : ''} muted · Show
        </Button>
      </Box>
    );
  }
  // --- END UPDATE

  return (
    <Collapse in={visibleAlerts.length > 0} animateOpacity>
      <Box
        position="absolute"
        top="16px"
        left="50%"
        transform="translateX(-50%)"
        zIndex={1400}
        w={{ base: '92%', md: 'auto' }}
        minW={{ md: '640px' }}
        maxW="800px"
        pointerEvents="auto"
        // --- UPDATE: accessibility + keyboard navigation
        role="region"
        aria-label="Flood inundation alerts"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        borderRadius="xl"
        _focusVisible={{ outline: '2px solid', outlineColor: focusRing, outlineOffset: '2px' }}
        {...containerProps}
        // --- END UPDATE
      >
        <Alert
          status="error"
          variant="solid"
          bg={theme.bg}
          borderRadius="xl"
          boxShadow={`0 10px 30px ${theme.shadow}`}
          borderWidth="1px"
          borderColor={theme.border}
          p={3}
          alignItems="flex-start"
          aria-live="assertive"
          aria-atomic="true"
        >
          <AlertIcon boxSize={5} as={theme.icon} mt={0.5} />
          <Box flex="1" minW={0}>
            <HStack spacing={2} mb={0.5} flexWrap="wrap">
              <AlertTitle fontSize="xs" fontWeight="800" letterSpacing="0.05em">
                {theme.title}
              </AlertTitle>
              <Badge colorScheme="whiteAlpha" color="white" fontSize="2xs" px={1.5}>
                {topAlert.wellName}
              </Badge>
              {/* --- UPDATE: reading context badges */}
              {levelLabel && (
                <Badge bg="whiteAlpha.300" color="white" fontSize="2xs" px={1.5}>
                  Level {levelLabel}
                </Badge>
              )}
              {topAlert.isStale && (
                <Tooltip label="This reading is older than the configured freshness window" hasArrow>
                  <Badge bg="yellow.300" color="gray.900" fontSize="2xs" px={1.5}>
                    STALE
                  </Badge>
                </Tooltip>
              )}

              {/* 🧠 AI PREDICTIVE FLOOD RISK & RAINFALL BADGES */}
              {topAlert.aiPrediction && (
                <>
                  <Tooltip label="Hydrological model flood probability based on rise velocity & silt capacity" hasArrow>
                    <Badge bg="purple.500" color="white" fontSize="2xs" px={1.5} borderRadius="md">
                      AI RISK {Math.round((topAlert.aiPrediction.floodRiskProbability || 0) * 100)}%
                    </Badge>
                  </Tooltip>

                  {topAlert.aiPrediction.estimatedMinutesToOvertopping !== null &&
                   topAlert.aiPrediction.estimatedMinutesToOvertopping !== undefined && (
                    <Badge 
                      bg={topAlert.aiPrediction.estimatedMinutesToOvertopping <= 30 ? "red.800" : "purple.700"} 
                      color="white" 
                      fontSize="2xs" 
                      px={1.5} 
                      borderRadius="md"
                    >
                      {topAlert.aiPrediction.estimatedMinutesToOvertopping === 0
                        ? "OVERFLOW IMMINENT"
                        : `OVERFLOW IN ~${topAlert.aiPrediction.estimatedMinutesToOvertopping}m`}
                    </Badge>
                  )}

                  {topAlert.aiPrediction.weatherInfluence?.precipitation_forecast_mm > 0 && (
                    <Tooltip label="Forecasted rainfall over next 6 hours (Open-Meteo)" hasArrow>
                      <Badge bg="blue.500" color="white" fontSize="2xs" px={1.5} borderRadius="md">
                        RAIN {topAlert.aiPrediction.weatherInfluence.precipitation_forecast_mm}mm
                      </Badge>
                    </Tooltip>
                  )}
                </>
              )}

              {visibleAlerts.length > 1 && (
                <Text fontSize="2xs" opacity={0.85} fontWeight="600">
                  {safeIndex + 1} of {visibleAlerts.length}
                </Text>
              )}
              {/* --- END UPDATE */}
            </HStack>
            <AlertDescription fontSize="xs" display="block" opacity={0.95}>
              Adjacent road corridors at risk of flood inundation:{' '}
              {/* --- UPDATE: compact text when collapsed, clickable chips when expanded */}
              {!isExpanded || !onFocusLocation ? (
                <Text as="span" fontWeight="700">
                  {affectedRoadsText}
                </Text>
              ) : (
                <Wrap spacing={1.5} mt={1.5}>
                  {roadsToShow.map((r) => (
                    <WrapItem key={String(r.roadId)}>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="whiteAlpha"
                        color="white"
                        fontWeight="700"
                        leftIcon={<Icon as={MdMyLocation} boxSize={3} />}
                        onClick={() => handleLocateRoad(r)}
                        aria-label={`Locate ${r.roadName}, ${r.distanceM} metres from ${topAlert.wellName}`}
                      >
                        {r.roadName} · {formatDistance(r.distanceM)}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
              {hiddenRoadCount > 0 && (
                <Button
                  variant="link"
                  size="xs"
                  color="white"
                  fontWeight="700"
                  ml={1}
                  rightIcon={<Icon as={MdExpandMore} />}
                  onClick={handleToggleExpanded}
                  aria-expanded={isExpanded}
                >
                  +{hiddenRoadCount} more
                </Button>
              )}
              {isExpanded && (
                <Button
                  variant="link"
                  size="xs"
                  color="white"
                  fontWeight="700"
                  ml={1}
                  rightIcon={<Icon as={MdExpandLess} />}
                  onClick={handleToggleExpanded}
                  aria-expanded={isExpanded}
                >
                  Show less
                </Button>
              )}
              {isExpanded && truncatedByModel > 0 && (
                <Text fontSize="2xs" opacity={0.8} mt={1}>
                  {truncatedByModel} further road{truncatedByModel > 1 ? 's' : ''} omitted (list cap).
                </Text>
              )}
              {/* --- END UPDATE */}
            </AlertDescription>

            {/* --- UPDATE: meta row — telemetry age, sensor, water mains, network summary */}
            <HStack spacing={3} mt={1.5} fontSize="2xs" opacity={0.85} flexWrap="wrap">
              {relativeTime && (
                <HStack spacing={1}>
                  <Icon as={MdAccessTime} boxSize={3} />
                  <Text>Reading {relativeTime}</Text>
                </HStack>
              )}
              {topAlert.sensorId !== undefined && topAlert.sensorId !== null && (
                <Text>Sensor {String(topAlert.sensorId)}</Text>
              )}
              {pipesForWell.length > 0 && (
                <Tooltip
                  hasArrow
                  label={pipesForWell
                    .slice(0, 6)
                    .map((p) => `${p.pipeName} (${formatDistance(p.distanceM)})`)
                    .join(', ')}
                >
                  <HStack spacing={1} cursor="default">
                    <Icon as={MdPlumbing} boxSize={3} />
                    <Text>
                      {pipesForWell.length} water main{pipesForWell.length > 1 ? 's' : ''} exposed
                    </Text>
                  </HStack>
                </Tooltip>
              )}
              {summary && summary.criticalWellCount > 1 && (
                <Text>
                  Network: {summary.criticalWellCount} wells · {summary.impactedRoadCount} roads
                  {summary.highRiskPipeCount ? ` · ${summary.highRiskPipeCount} mains` : ''}
                </Text>
              )}
            </HStack>
            {/* --- END UPDATE */}
          </Box>

          {/* --- UPDATE: action cluster */}
          <HStack spacing={1} ml={3} flexShrink={0} alignSelf="center">
            {visibleAlerts.length > 1 && (
              <>
                <IconButton
                  size="xs"
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  icon={<Icon as={MdChevronLeft} boxSize={4} />}
                  onClick={handlePrev}
                  aria-label="Previous flood alert"
                />
                <IconButton
                  size="xs"
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  icon={<Icon as={MdChevronRight} boxSize={4} />}
                  onClick={handleNext}
                  aria-label="Next flood alert"
                />
              </>
            )}
            {/* --- END UPDATE */}
            {onFocusLocation && (
              <Button
                size="xs"
                colorScheme="whiteAlpha"
                variant="outline"
                color="white"
                ml={3}
                onClick={handleLocateWell}
              >
                Locate
              </Button>
            )}
            {/* --- UPDATE */}
            {onDispatchCrew && (
              <Button
                size="xs"
                bg="white"
                color={theme.bg}
                _hover={{ bg: 'whiteAlpha.900' }}
                leftIcon={<Icon as={MdBuild} boxSize={3} />}
                onClick={handleDispatch}
              >
                Dispatch
              </Button>
            )}
            {onViewDetails && (
              <IconButton
                size="xs"
                variant="ghost"
                color="white"
                _hover={{ bg: 'whiteAlpha.300' }}
                icon={<Icon as={MdInfoOutline} boxSize={4} />}
                onClick={handleViewDetails}
                aria-label={`View details for ${topAlert.wellName}`}
              />
            )}
            {isDismissible && (
              <Tooltip label="Mute until this alert changes (Esc)" hasArrow>
                <IconButton
                  size="xs"
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  icon={<Icon as={MdClose} boxSize={4} />}
                  onClick={handleDismiss}
                  aria-label={`Dismiss flood alert for ${topAlert.wellName}`}
                />
              </Tooltip>
            )}
          </HStack>
          {/* --- END UPDATE */}
        </Alert>
      </Box>
    </Collapse>
  );
};

export default React.memo(FloodInundationAlert);